import {
  ChartBar,
  ChartLineUp,
  CursorClick,
  DownloadSimple,
  HandCoins as HandCoinsIcon,
  Megaphone,
  Money,
  TrendUp,
} from "@phosphor-icons/react";
import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import AnimatedModal from "../../../../components/AnimatedModal";
import InlineEdit from "../../../../components/InlineEdit";
import CampaignDetailSkeleton from '../../../../components/SkeletonLoaders/CampaignDetailSkeleton';
import StatusSelect from "../../../../components/StatusSelect";
import { useDateRange } from "../../../../hooks";
import { useCampaignDetail } from "../../../../hooks/get/useCampaignDetail";
import { useUpdateCampaignBid } from "../../../../hooks/patch/useUpdateCampaignBid";
import { useUpdateCampaignBudget } from "../../../../hooks/patch/useUpdateCampaignBudget";
import { useUpdateCampaignStatus } from "../../../../hooks/post/useUpdateCampaignStatus";
import { IDateRangePreset } from "../../../../interfaces";
import { cn, getAmountByCurrency, getCurrencySymbol } from "../../../../lib/utils";
import { CurrencyCode, GOOGLE_CAMPAIGN_STATUS } from "../../../../shared/constants";
import PerformanceChart from "../../components/PerformanceChart";
import BaseCampaignDetail from "../../components/shared/BaseCampaignDetail";
import { IGoogleAdsDailyMetric } from "../../interface";
import { IMetaDailyMetric } from "../../interface/metaAds";

const GoogleAdsCampaignDetail: React.FC = () => {
  const { campaign_id } = useParams<{ campaign_id: string }>();
  const navigate = useNavigate();
  const { dateRange, setDateRange, selectedPreset, setSelectedPreset } = useDateRange();
  const { t } = useTranslation();

  // Modal states
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<GOOGLE_CAMPAIGN_STATUS | null>(null);
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [pendingBudget, setPendingBudget] = useState<string>("");
  const [showBidModal, setShowBidModal] = useState(false);
  const [pendingBid, setPendingBid] = useState<string>("");
  const [isActionLoading, setIsActionLoading] = useState(false);

  const updateBudgetMutation = useUpdateCampaignBudget();
  const updateBidMutation = useUpdateCampaignBid();
  const updateStatusMutation = useUpdateCampaignStatus();

  const handleDateRangeChange = (start: Date, end: Date) => {
    setDateRange(start, end);
  };

  const handlePresetChange = (preset: IDateRangePreset | undefined) => {
    setSelectedPreset(preset);
  };

  const { data, isLoading, isError, refetch } = useCampaignDetail({
    campaignId: campaign_id || "",
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
  });

  const campaignDetail = data;
  const campaign = campaignDetail?.campaign;
  const currency = (((campaign)?.default_currency || campaign?.currency) as CurrencyCode) || "USD";

  const biddingStrategy = campaign?.bidding_strategy ?? null;
  const isTargetRoas = biddingStrategy === "TARGET_ROAS";
  const bidValue = isTargetRoas
    ? (campaign?.target_roas ?? 0) * 100
    : (campaign?.target_cpa ?? 0);
  const bidPrefix = isTargetRoas ? undefined : getCurrencySymbol(currency as CurrencyCode);
  const bidSuffix = isTargetRoas ? "%" : undefined;

  const handleUpdateBudget = (newValue: string) => {
    setPendingBudget(newValue);
    setShowBudgetModal(true);
    return Promise.resolve();
  };

  const handleConfirmBudgetChange = async () => {
    setIsActionLoading(true);
    try {
      await updateBudgetMutation.mutateAsync({
        campaign_id: campaign_id || "",
        google_campaign_id: campaign?.google_campaign_id || campaign?.campaign_id || "",
        google_campaign_budget_id: (campaign)?.google_campaign_budget_id || "",
        google_campaign_budget: parseFloat(pendingBudget),
      });
      toast.success(t("common.manageCampaign.budgetUpdatedSuccessfully"));
      setShowBudgetModal(false);
      refetch();
    } catch {
      toast.error(t("common.manageCampaign.errors.failedToUpdateBudget"));
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleUpdateBid = (newValue: string) => {
    setPendingBid(newValue);
    setShowBidModal(true);
    return Promise.resolve();
  };

  const handleConfirmBidChange = async () => {
    setIsActionLoading(true);
    try {
      await updateBidMutation.mutateAsync({
        new_bid: parseFloat(pendingBid),
        current_bid: bidValue,
        google_campaign_id: campaign?.campaign_id || "",
        campaign_id: campaign?.id || "",
        bidding_strategy: biddingStrategy ?? "TARGET_CPA",
      });
      toast.success(t("common.manageCampaign.bidUpdatedSuccessfully"));
      setShowBidModal(false);
      refetch();
    } catch {
      toast.error(t("common.manageCampaign.errors.failedToUpdateBid"));
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleStatusSwitch = (value: GOOGLE_CAMPAIGN_STATUS) => {
    setPendingStatus(value);
    setShowStatusModal(true);
  };

  const handleConfirmStatusChange = async () => {
    if (!pendingStatus) return;
    setIsActionLoading(true);
    try {
      await updateStatusMutation.mutateAsync({
        campaign_id: campaign_id || "",
        google_campaign_id: campaign?.google_campaign_id || campaign?.campaign_id || "",
        status: pendingStatus,
      });
      toast.success(t("common.manageCampaign.statusUpdatedSuccessfully"));
      setShowStatusModal(false);
      refetch();
    } catch {
      toast.error(t("common.manageCampaign.errors.failedToUpdateStatus"));
    } finally {
      setIsActionLoading(false);
    }
  };

  useEffect(() => {
    if (!isLoading && isError) {
      toast.error(t("campaigns.errors.integrationChangedRedirect", "Campaign not found for the selected integration. Redirecting you back to the campaign list."));
      navigate(-1);
    }
  }, [isLoading, isError, navigate, t]);

  if (!isLoading && isError) return null;

  const isEnabled = campaign?.status === GOOGLE_CAMPAIGN_STATUS.ENABLED;

  const metricsRecord = campaignDetail?.metrics as Record<string, number> | undefined;
  const campaignRecord = campaign as Record<string, number | string | null | undefined> | undefined;

  const chartData: IMetaDailyMetric[] = (campaignDetail?.daily_metrics || []).map((m: IGoogleAdsDailyMetric) => {
    const rowCost = getAmountByCurrency(m, "cost", currency);
    return {
      date: m.date,
      reach: 0,
      country_code: m.country_code || "",
      spend_usd: rowCost,
      spend_inr: m.cost_inr,
      impressions: m.impressions,
      clicks: m.clicks,
      ctr: m.clicks && m.impressions ? (m.clicks / m.impressions) * 100 : 0,
      cpc_usd: m.clicks ? rowCost / m.clicks : 0,
      cpm_usd: m.impressions ? (rowCost / m.impressions) * 1000 : 0,
    };
  });

  if (isLoading) return <CampaignDetailSkeleton />;

  return (
    <BaseCampaignDetail
      headerProps={{
        title: campaign?.campaign_name || t("campaigns.detail.title", "Campaign Details"),
        platform: "google_ads",
        type: "detail",
        appInfo: {
          id: campaign?.campaign_id || "",
          name: campaign?.app_name || "",
          logoUrl: campaign?.app_logo || null,
        },
        pillSwitch: (
          <StatusSelect
            value={isEnabled ? GOOGLE_CAMPAIGN_STATUS.ENABLED : GOOGLE_CAMPAIGN_STATUS.PAUSED}
            options={[
              {
                label: t("common.manageCampaign.paused", "Paused"),
                value: GOOGLE_CAMPAIGN_STATUS.PAUSED,
                color: "red",
              },
              {
                label: t("common.manageCampaign.enabled", "Enabled"),
                value: GOOGLE_CAMPAIGN_STATUS.ENABLED,
                color: "green",
              },
            ]}
            onChange={handleStatusSwitch}
          />
        ),
        onHistoryClick: () => navigate(`/campaigns/google-ads/${campaign_id}/history`),
        onDateRangeChange: handleDateRangeChange,
        onPresetChange: handlePresetChange,
        dateRange: dateRange,
        selectedPresetId: selectedPreset?.id,
      }}
      statsCounts={7}
      stats={[
        {
          title: t("campaigns.metrics.totalSpend", "Total Spend"),
          value: getAmountByCurrency(metricsRecord || {}, "total_cost", currency),
          isCurrency: true,
          icon: <Money size={20} className="text-gray-700 dark:text-gray-300" weight="duotone" />,
        },
        {
          title: t("campaigns.metrics.installs", "Installs"),
          value: campaignDetail?.metrics?.total_conversions || 0,
          icon: <DownloadSimple size={20} className="text-gray-700 dark:text-gray-300" weight="duotone" />,
        },
        {
          title: t("campaigns.metrics.impressions", "Impressions"),
          value: campaignDetail?.metrics?.total_impressions || 0,
          icon: <ChartBar size={20} className="text-gray-700 dark:text-gray-300" weight="duotone" />,
        },
        {
          title: t("campaigns.metrics.clicks", "Clicks"),
          value: campaignDetail?.metrics?.total_clicks || 0,
          icon: <CursorClick size={20} className="text-gray-700 dark:text-gray-300" weight="duotone" />,
        },
        {
          title: t("campaigns.metrics.ctr", "CTR"),
          value: (campaignDetail?.metrics?.average_ctr || 0) * 100,
          isPercentage: true,
          icon: <TrendUp size={20} className="text-gray-700 dark:text-gray-300" weight="duotone" />,
        },
        {
          title: t("campaigns.metrics.avgCpc", "Avg. CPC"),
          value: getAmountByCurrency(metricsRecord || {}, "average_cpc", currency),
          isCurrency: true,
          icon: <HandCoinsIcon size={20} className="text-gray-700 dark:text-gray-300" weight="duotone" />,
        },
        {
          title: t("campaigns.metrics.conversions", "Conversions"),
          value: campaignDetail?.metrics?.total_conversions || 0,
          icon: <ChartLineUp size={20} className="text-gray-700 dark:text-gray-300" weight="duotone" />,
        }
      ]}
      currency={currency as CurrencyCode}
      chart={
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold flex items-center gap-2 text-gray-800 dark:text-gray-200">
              <ChartLineUp size={20} className="text-gray-700 dark:text-gray-300" weight="duotone" />
              {t("campaigns.detail.performanceOverTime", "Performance Over Time")}
            </h3>
          </div>
          <div className="h-[350px]">
            {chartData.length > 0 ? (
              <PerformanceChart
                height={350}
                currency={currency}
                currencySymbol={getCurrencySymbol(currency as CurrencyCode)}
                data={chartData}
              />
            ) : (
              <div className="h-full flex items-center justify-center bg-gray-50 dark:bg-neutral-800 rounded-xl border border-dashed border-gray-200 dark:border-neutral-700">
                <span className="text-sm text-gray-400 dark:text-gray-500">
                  {t("campaigns.detail.noDailyMetrics", "No daily metrics available for this period")}
                </span>
              </div>
            )}
          </div>
        </div>
      }
      sidebar={
        <div className="flex flex-col gap-6">
          <h3 className="text-sm font-bold flex items-center gap-2 text-gray-800 dark:text-gray-200">
            <TrendUp size={20} className="text-gray-700 dark:text-gray-300" weight="duotone" />
            {t("campaigns.detail.campaignSettings", "Campaign Settings")}
          </h3>
          <div className="flex flex-col gap-4">
            <InfoRow
              label={t("campaigns.detail.dailyBudget", "Daily Budget")}
              value={
                <InlineEdit
                  value={getAmountByCurrency(campaignRecord || {}, "daily_budget", currency) || Number(campaign?.daily_budget || 0)}
                  onSave={handleUpdateBudget}
                  prefix={getCurrencySymbol(currency as CurrencyCode)}
                  type="number"
                  editIconPlacement="start"
                  alwaysShowEdit
                />
              }
            />
            <InfoRow
              label={t("campaigns.detail.bid", "Bid")}
              value={
                <InlineEdit
                  value={bidValue}
                  onSave={handleUpdateBid}
                  prefix={bidPrefix}
                  suffix={bidSuffix}
                  type="number"
                  editIconPlacement="start"
                  alwaysShowEdit
                />
              }
            />
            <InfoRow
              label={t("campaigns.detail.biddingStrategy", "Bidding Strategy")}
              value={
                campaign?.bidding_strategy
                  ? campaign.bidding_strategy === "TARGET_ROAS"
                    ? "Target ROAS"
                    : "Target CPA"
                  : "-"
              }
            />
            <InfoRow
              label={t("campaigns.detail.objective", "Objective")}
              value={"APP"}
              capitalize
            />
            <InfoRow
              label={t("campaigns.detail.campaignType", "Campaign Type")}
              value={campaign?.campaign_type?.replace(/_/g, " ") || "-"}
              capitalize
            />
            <InfoRow
              label={t("campaigns.detail.platform", "Platform")}
              value={campaign?.app_platform || "-"}
              capitalize
            />
            <InfoRow
              label={t("campaigns.detail.currency", "Currency")}
              value={campaign?.currency || "-"}
            />
            {(campaign?.countries?.length ?? 0) > 0 && (
              <InfoRow
                label={t("campaigns.detail.countries", "Countries")}
                value={
                  campaign!.countries.length <= 3
                    ? campaign!.countries.join(", ")
                    : `${campaign!.countries.slice(0, 3).join(", ")} +${campaign!.countries.length - 3} more`
                }
              />
            )}
            <InfoRow
              label={t("campaigns.detail.created", "Created")}
              value={
                campaign?.created_at
                  ? new Date(campaign.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
                  : "-"
              }
            />
          </div>
        </div>
      }
    >
      {/* {(campaignDetail?.country_breakdown?.length ?? 0) > 0 && (
        <CountryBreakdownSection
          data={campaignDetail!.country_breakdown!}
          currency={currency as CurrencyCode}
        />
      )} */}

      {/* Confirmation Modals */}
      <AnimatedModal
        show={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        title={t("common.manageCampaign.confirmStatusChange", "Confirm Status Change")}
        isConfirmation
        onConfirm={handleConfirmStatusChange}
        isConfirming={isActionLoading}
        confirmButtonColor={pendingStatus === GOOGLE_CAMPAIGN_STATUS.ENABLED ? "green" : "red"}
      >
        <div className="flex flex-col items-center gap-4 py-4 text-center">
          <div className={`p-3 rounded-full ${pendingStatus === GOOGLE_CAMPAIGN_STATUS.ENABLED ? "bg-green-100 text-green-600" : "bg-red-100 text-red-600"}`}>
            <Megaphone size={32} weight="duotone" />
          </div>
          <p className="text-gray-600 dark:text-gray-400">
            {t("common.manageCampaign.statusChangeConfirmation", `Are you sure you want to ${pendingStatus === GOOGLE_CAMPAIGN_STATUS.ENABLED ? "enable" : "pause"} this campaign?`)}
          </p>
        </div>
      </AnimatedModal>

      <AnimatedModal
        show={showBudgetModal}
        onClose={() => setShowBudgetModal(false)}
        title={t("common.manageCampaign.confirmBudgetChange", "Confirm Budget Change")}
        isConfirmation
        onConfirm={handleConfirmBudgetChange}
        isConfirming={isActionLoading}
      >
        <div className="flex flex-col items-center gap-4 py-4 text-center">
          <div className="p-3 rounded-full bg-indigo-100 text-indigo-600">
            <Money size={32} weight="duotone" />
          </div>
          <div>
            <p className="text-gray-600 dark:text-gray-400 mb-2">
              {t("common.manageCampaign.budgetChangeConfirmation", "Are you sure you want to update the daily budget?")}
            </p>
            <div className="flex items-center justify-center gap-3 font-bold text-lg">
              <span className="text-indigo-600">
                {getCurrencySymbol(currency as CurrencyCode)}
                {(parseFloat(pendingBudget) || 0).toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      </AnimatedModal>

      <AnimatedModal
        show={showBidModal}
        onClose={() => setShowBidModal(false)}
        title={t("common.manageCampaign.confirmBidChange", "Confirm Bid Change")}
        isConfirmation
        onConfirm={handleConfirmBidChange}
        isConfirming={isActionLoading}
      >
        <div className="flex flex-col items-center gap-4 py-4 text-center">
          <div className="p-3 rounded-full bg-indigo-100 text-indigo-600">
            <CursorClick size={32} weight="duotone" />
          </div>
          <div>
            <p className="text-gray-600 dark:text-gray-400 mb-2">
              {t("common.manageCampaign.bidChangeConfirmation", "Are you sure you want to update the bid?")}
            </p>
            <div className="flex items-center justify-center gap-3 font-bold text-lg">
              <span className="text-indigo-600">
                {isTargetRoas
                  ? `${(parseFloat(pendingBid) || 0).toLocaleString()}%`
                  : `${getCurrencySymbol(currency as CurrencyCode)}${(parseFloat(pendingBid) || 0).toLocaleString()}`
                }
              </span>
            </div>
          </div>
        </div>
      </AnimatedModal>
    </BaseCampaignDetail>
  );
};

const InfoRow = ({ label, value, capitalize = false }: { label: string; value: React.ReactNode; capitalize?: boolean }) => (
  <div className="flex justify-between items-center py-2 border-b border-gray-50 dark:border-neutral-700 last:border-0 min-h-[44px]">
    <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">{label}</span>
    <span className={cn("text-sm font-bold text-gray-800 dark:text-gray-100 truncate pl-4", capitalize && "capitalize")}>
      {value}
    </span>
  </div>
);

// const INITIAL_VISIBLE = 10;

// const getCountryFlag = (code: string): string => {
//   if (!code || code.length !== 2) return "🌐";
//   return String.fromCodePoint(
//     ...([...code.toUpperCase()].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65))
//   );
// };

// interface CountryBreakdownSectionProps {
//   data: ICountryBreakdownItem[];
//   currency: CurrencyCode;
// }

// const CountryBreakdownSection: React.FC<CountryBreakdownSectionProps> = ({ data, currency }) => {
//   const [showAll, setShowAll] = React.useState(false);

//   const getSpend = (row: ICountryBreakdownItem) =>
//     getAmountByCurrency(row as unknown as Record<string, number>, "cost", currency);

//   const sorted = [...data].sort((a, b) => getSpend(b) - getSpend(a));

//   const maxSpend = sorted.length > 0 ? getSpend(sorted[0]) : 1;

//   const visible = showAll ? sorted : sorted.slice(0, INITIAL_VISIBLE);
//   const hiddenCount = sorted.length - INITIAL_VISIBLE;

//   const fmtNum = (n: number) =>
//     n >= 1_000_000
//       ? `${(n / 1_000_000).toFixed(1)}M`
//       : n >= 1_000
//         ? `${(n / 1_000).toFixed(1)}K`
//         : String(n);

//   return (
//     <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
//       <div className="flex items-center justify-between mb-5">
//         <h3 className="text-sm font-bold flex items-center gap-2 text-gray-800 dark:text-gray-200">
//           <span className="text-base leading-none">🌍</span>
//           Country Breakdown
//         </h3>
//         <span className="text-xs text-gray-400 font-medium">{sorted.length} countries</span>
//       </div>

//       {/* Table header */}
//       <div className="grid grid-cols-[28px_1fr_160px_72px_88px_72px] gap-x-3 px-2 pb-2 border-b border-gray-100">
//         <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">#</span>
//         <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Country</span>
//         <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Spend</span>
//         <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider text-right">Clicks</span>
//         <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider text-right">Impressions</span>
//         <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider text-right">Conv.</span>
//       </div>

//       {/* Rows */}
//       <div className="divide-y divide-gray-50">
//         {visible.map((row, idx) => {
//           const spend = getSpend(row);
//           const pct = maxSpend > 0 ? (spend / maxSpend) * 100 : 0;
//           const label = row.country_code || "Unknown";
//           const flag = getCountryFlag(row.country_code);

//           return (
//             <div
//               key={row.country_code || `unknown-${idx}`}
//               className="grid grid-cols-[28px_1fr_160px_72px_88px_72px] gap-x-3 px-2 py-3 items-center hover:bg-gray-50/60 transition-colors"
//             >
//               {/* Rank */}
//               <span className="text-xs font-semibold text-gray-300 tabular-nums">
//                 {idx + 1}
//               </span>

//               {/* Country */}
//               <div className="flex items-center gap-2 min-w-0">
//                 <span className="text-base leading-none">{flag}</span>
//                 <span className="text-xs font-semibold text-gray-700 truncate">
//                   {label}
//                 </span>
//               </div>

//               {/* Spend with bar */}
//               <div className="flex flex-col gap-1">
//                 <span className="text-xs font-bold text-gray-800 tabular-nums">
//                   {getCurrencySymbol(currency)}{spend.toLocaleString(undefined, { maximumFractionDigits: 2 })}
//                 </span>
//                 <div className="h-1 rounded-full bg-gray-100 overflow-hidden">
//                   <div
//                     className="h-full rounded-full bg-indigo-500"
//                     style={{ width: `${pct}%` }}
//                   />
//                 </div>
//               </div>

//               {/* Clicks */}
//               <span className="text-xs font-semibold text-gray-600 tabular-nums text-right">
//                 {fmtNum(row.clicks)}
//               </span>

//               {/* Impressions */}
//               <span className="text-xs font-semibold text-gray-600 tabular-nums text-right">
//                 {fmtNum(row.impressions)}
//               </span>

//               {/* Conversions */}
//               <span className="text-xs font-semibold text-gray-600 tabular-nums text-right">
//                 {fmtNum(row.conversions)}
//               </span>
//             </div>
//           );
//         })}
//       </div>

//       {/* Show more / less */}
//       {sorted.length > INITIAL_VISIBLE && (
//         <button
//           onClick={() => setShowAll((v) => !v)}
//           className="mt-4 w-full text-xs font-semibold text-indigo-600 hover:text-indigo-800 py-2 rounded-xl hover:bg-indigo-50 transition-colors"
//         >
//           {showAll ? "Show less" : `Show all ${hiddenCount} more countries`}
//         </button>
//       )}
//     </div>
//   );
// };

export default GoogleAdsCampaignDetail;
